package com.neurobs.ccab

import com.neurobs.ccab.db.TestRunManager.TestRunEvent
import com.neurobs.util._
import io.circe._
import io.circe.generic.auto._
import com.neurobs.util.JsonPrinter.jsonPrint
import com.neurobs.util.SecondsTimestamp._

import scala.util.{Failure, Success, Try} // for Circe decoder/encoder

object MessageType extends Enumeration {
	val GetControllerState = Value("get_controller_state")
	val ControllerState = Value("controller_state")

	val GetCommState = Value("get_comm_state")
	val CommState = Value("comm_state")

	val GetResumableSessions = Value("get_resumable_sessions")
	val ResumableSessions = Value("resumable_sessions")

	val SetComment = Value("set_comment")
	val SetTags = Value("set_tags")

	val ResetCommunication = Value("reset_communication")

	val GetTestPackageData = Value("get_test_package_data")
	val TestPackageData = Value("test_package_data")

	val SetSession = Value("set_session")
	val SetManagedSession = Value("set_managed_session")
	val ResumeManagedSession = Value("resume_managed_session")
	val EndSession = Value("end_session")

	val SetTestRunHistory = Value("set_test_run_history")
	val SetTestMetadata = Value("set_test_metadata")

	val GetSessionData = Value("get_session_data")
	val SessionData = Value("session_data")

	val GetPowerStatus = Value("get_power_status")
	val PowerStatus = Value("power_status")

	val SetChatVolume = Value("set_chat_vol")
	val ChatVolume = Value("chat_vol")

	val SetAttenuation = Value("set_att")

	val GetState = Value("get_state")
	val State = Value("state")

	val Performance = Value("perf")

	val GetTestDetail = Value("get_test_detail")
	val TestDetail = Value("test_detail")

	val SetPauseBetweenTrue = Value("set_pause_between_true")
	val SetPauseBetweenFalse = Value("set_pause_between_false")

	val ResumeSequence = Value("resume_sequence")
	val PauseSequence = Value("pause_sequence")
	val QuitSequence = Value("quit_sequence")

	val StartTest = Value("start_test")
	val QuitTest = Value("quit_test")

	val Notification = Value("notification")

	val CommandError = Value("command_error")
	val ControllerError = Value("controller_error")
	val ShutdownController = Value("shutdown_controller")

	val SetSubjectAV = Value("set_subject_av")
	val SetHelpRequested = Value("set_help_requested")
	val ClearHelpRequested = Value("clear_help_requested")

	val GetDataSyncState = Value("get_data_sync_state")
	val DataSyncState = Value("data_sync_state")
	val SetDataSyncPause = Value("set_data_sync_pause")
	val AddDataSyncBacklog = Value("add_data_sync_backlog")

	val SetUploadCredentials = Value("set_upload_credentials")

	val Ping = Value("ping")
	val Pong = Value("pong")

	def withNameOpt(s: String): Option[Value] = values.find(_.toString == s)
}

sealed trait MessageData {
  def `type`: MessageType.Value
}

case class BasicMessage
(
  override val `type`: MessageType.Value,
) extends MessageData

object SequenceStatus extends Enumeration {
	val Stopped = Value("Stopped")
	val Running = Value("Running")
	val Paused = Value("Paused")
	val Finished = Value("Finished")
}

object TestStatus extends Enumeration {
  val Running = Value("running")
  val Paused = Value("paused")
  val Finished = Value("finished")
}

object PresentationConnectionStatus extends Enumeration {
	val Connecting = Value("connecting")
	val Connected = Value("connected")
	val Disconnected = Value("disconnected")
}

object ParameterType extends Enumeration {
	val Standard = Value("standard")
	val Review = Value("review")
	val Start = Value("start")
}

object ParameterDataType extends Enumeration {
	val Bool = Value("bool")
	val BoolArray = Value("boola")
	val Color = Value("col")
	val ColorArray = Value("cola")
	val Float = Value("float")
	val FloatArray = Value("floata")
	val Int = Value("int")
	val IntArray = Value("inta")
	val String = Value("string")
	val StringArray = Value("stringa")
}

object PauseType extends Enumeration {
	val Pause = Value("pause")
	val Resume = Value("resume")
}

object SyncRequestType extends Enumeration {
	val TestRunCreate = Value("run")
	val File = Value("file")
	val SessionLog = Value("slog")
}

object SyncRequestStatus extends Enumeration {
  val Pending = Value("pending")
  val Succeeded = Value("succeeded")
  val GeneralError = Value("error")
  val FatalError = Value("fatal_error")
}

// Note that the following case class member names must match
// the names used in the JSON

case class SyncRequestStatus
(
	`type`: SyncRequestType.Value,
	file: String,
	status: Option[SyncRequestStatus.Value],
	done: Option[Boolean],
	durs: Option[Double],
	error: Option[String]
) {
	def getStatus(): SyncRequestStatus.Value = status match {
		case Some(s) => s
		case None => if (done.getOrElse(false)) SyncRequestStatus.Succeeded else error match {
			case Some(_) => SyncRequestStatus.GeneralError
			case None => SyncRequestStatus.Pending
		}
	}
	def finished(): Boolean = getStatus() match {
		case SyncRequestStatus.Pending => false
		case SyncRequestStatus.Succeeded => true
		case SyncRequestStatus.GeneralError => false
		case SyncRequestStatus.FatalError => true
	}
}

case class TestConnectionState
(
	status: PresentationConnectionStatus.Value,
	test_sys: Option[String],
	error: Option[String]
) {
	def localPresentation: Option[Boolean] = test_sys.map(_ == "In-process Server")
}

case class PauseEvent
(
	time: SecondsTimestamp,
	`type`: PauseType.Value
)

case class ParameterValue
(
	name: String,
	value: String
)

case class Test
(
	name: String,
	starts: List[String],
	default_start: Option[String]
)

case class SequenceElement
(
	id: SequenceElement.Id,
	/** Index of test in group's test list */
	index: Int
)

object SequenceElement {
	type Id = Int
}

case class SequenceState
(
	name: String,
	tests: List[SequenceElement],
	next_index: Int
) {
	def finished: Boolean = next_index >= tests.length

	def nextTest(g: Group): Option[Test] = if (finished) None else Some(g.tests(tests(next_index).index))
}

object SequenceState {
	def empty = SequenceState(name = "", tests = List(), next_index = 0)
}

case class Parameter
(
	name: String,
	`type`: ParameterType.Value,
	dtype: ParameterDataType.Value,
	values: List[String],
	default: Option[String]
)

case class Group
(
	name: String,
	tests: List[Test],
	configs: List[String],
	params: List[Parameter],
	seqs: List[String]
)

object Group {
	def empty = Group(name = "", tests = List(), configs = List(), params = List(), seqs = List())
}

case class GroupOptions
(
	name: String,
	configs: List[String],
	params: List[Parameter],
	seqs: List[String]
)

case class GroupSettings
(
	config: Option[String],
	params: List[ParameterValue]
)

case class TestRun
(
	/** Index of test in group's test list */
	test: Int,
	seq_id: Option[SequenceElement.Id],
	error: Option[String]
)

case class TestStat
(
	name: String,
	value: String
)

case class TestCriterion
(
	name: String,
	required: String,
	value: String,
	status: String
)

object TestGraphType extends Enumeration {
	val Bar = Value("bar")
	val Line = Value("line")
}

case class TestGraph
(
	title: String,
	`type`: TestGraphType.Value,
	x: List[String],
	y: List[String]
)

case class TestPerformance
(
	section: String,
	section_start_time: Float,
	section_time: Float,
	values: Option[List[TestStat]],
	criteria: Option[List[TestCriterion]],
	graphs: Option[List[TestGraph]]
)

case class TestStop
(
	section: String,
	section_start_time: Float,
	section_time: Float,
	reason: String
)

case class NoiseMetrics
(
	pwr: Double,
	snr: Double
)

case class TestRunDetail
(
	run: TestRun,
	start_time: SecondsTimestamp,
	end_time: Option[SecondsTimestamp],
	start: Option[String],
	status: TestStatus.Value,
	pauses: Option[List[PauseEvent]],
	perf: List[TestPerformance],
	comments: Option[List[ExaminerComment]],
	tags: Option[List[String]],
	stop: Option[TestStop],
	nse: Option[NoiseMetrics]
)

case class SessionState
(
	session_id: String,
	group_set: GroupSettings,
	seq: SequenceState,
	log: List[TestRun],
	last_log: Option[TestRunDetail],
	pause_between: Option[Boolean],  // This is an Option for backward compatibility
	attenuation: Option[Double],
	comments: Option[List[ExaminerComment]],
	tags: Option[List[String]],
	test_log: Option[List[TestRunDetail]]
) {
	def stopped: Boolean = last_log.map(_.status == TestStatus.Finished).getOrElse(true)

	def sequenceStatus: SequenceStatus.Value = {
		if (stopped) {
			if (seq.next_index < seq.tests.length) SequenceStatus.Stopped
			else SequenceStatus.Finished
		}
		else {
			if (last_log.get.status == TestStatus.Running) SequenceStatus.Running
			else SequenceStatus.Paused
		}
	}
}

case class ControllerStateData
(
	test_status: String,
	software_version: String,
	connection: TestConnectionState,
	session_active: Boolean
)

case class ControllerStateMessage
(
	override val `type`: MessageType.Value = MessageType.ControllerState,
	data: ControllerStateData,
) extends MessageData

case class TestPackageData
(
	test_ver: Option[String],
	groups: List[GroupOptions]
)

case class TestPackageDataMessage
(
	override val `type`: MessageType.Value = MessageType.TestPackageData,
	data: TestPackageData
) extends MessageData

case class SessionData
(
	subject_id: String,
	subject_name: String,
	session_id: String,
	reporting_id: Option[Guid],
	start_time: SecondsTimestamp,
	test_version: Option[String],
	group: Group
)

case class SessionDataMessage
(
	override val `type`: MessageType.Value = MessageType.SessionData,
	data: SessionData
) extends MessageData

case class SessionLogStorage
(
	session: SessionData,
	state: SessionState
)
{
	def hasComments: Boolean =
		state.comments.exists(_.nonEmpty) || state.test_log.exists(_.exists(_.comments.exists(_.nonEmpty)))
	def hasTestComments: Boolean =
		state.test_log.exists(_.exists(_.comments.exists(_.nonEmpty)))

	/** Restores some members that are excluded for stored session logs */
	def reconstructed: SessionLogStorage = SessionLogStorage(
		session = session,
		state = state.copy(
			log = state.test_log.map(_.map(_.run)).getOrElse(List()),
			last_log = state.test_log.flatMap(_.lastOption)
		)
	)
}

case class StateData
(
	con: TestConnectionState,
	session: Option[SessionState],
	help_req: Boolean,
	chat_vol: Option[Double]
)

case class StateDataMessage
(
	override val `type`: MessageType.Value = MessageType.State,
	data: StateData
) extends MessageData

case class SetSubjectAVData
(
	video: Boolean,
	audio: Boolean
)

case class SetSubjectAVMessage
(
	override val `type`: MessageType.Value = MessageType.SetSubjectAV,
	data: SetSubjectAVData
) extends MessageData

case class SetChatVolumeData
(
	volume: Option[Double] = None,
	change: Option[Double] = None
)

case class SetChatVolumeMessage
(
	override val `type`: MessageType.Value = MessageType.SetChatVolume,
	data: SetChatVolumeData
) extends MessageData

case class ChatVolumeData
(
	volume: Double
)

case class ChatVolumeMessage
(
	override val `type`: MessageType.Value = MessageType.ChatVolume,
	data: ChatVolumeData
) extends MessageData

case class SetAttenuationData
(
	attenuation: Double
)

case class SetAttenuationMessage
(
	override val `type`: MessageType.Value = MessageType.SetAttenuation,
	data: SetAttenuationData
) extends MessageData

case class SetSessionData
(
	group_name: String,
	configuration: Option[Int],
	parameters: List[ParameterValue],
	sequence_name: String,
	subject_name: String
)

case class SetSessionMessage
(
	override val `type`: MessageType.Value = MessageType.SetSession,
	data: SetSessionData
) extends MessageData

case class ResetCommunicationData
(
	api_override: Option[CaasType.Value]
)

case class ResetCommunicationMessage
(
	override val `type`: MessageType.Value = MessageType.ResetCommunication,
	data: ResetCommunicationData
) extends MessageData

case class MetadataItem
(
	name: String,
	value: String
)

case class LogfileMetadataData
(
	items: Seq[MetadataItem]
)

object LogfileMetadataData {
	def apply(): LogfileMetadataData = LogfileMetadataData(items = Seq())
}

case class SubjectPropertiesData
(
	birthdate: Option[PlainDate],
	birth_year: Int,
	sex: Sex.Value,
)

object SubjectPropertiesData {
	def apply(d: SubjectIdentityData): SubjectPropertiesData = SubjectPropertiesData(birthdate = d.birthdate, birth_year = d.birthYear, sex = d.sex)
}

case class SetManagedSessionData
(
	test_session_id: Long,
	start_time: SecondsTimestamp,
	study_name: String,
	access_guid: Guid,
	subject_guid: Guid,
	reporting_id: Guid,
	account_subject_id: Long,
	settings: SetSessionData,
	metadata: LogfileMetadataData,
	identity: Option[SubjectPropertiesData]
)

case class SetManagedSettingsMessage
(
	override val `type`: MessageType.Value = MessageType.SetManagedSession,
	data: SetManagedSessionData
) extends MessageData

case class SetTestRunHistoryData
(
	append: Boolean,
	history: Seq[TestRunEvent]
)

case class SetTestRunHistoryMessage
(
	override val `type`: MessageType.Value = MessageType.SetTestRunHistory,
	data: SetTestRunHistoryData
) extends MessageData

case class SetTestMetadataData
(
	props: Seq[SetTestMetadataData.Property]
)
object SetTestMetadataData {
	case class PropertyValue
	(
		value: String,
		tests: Seq[String]
	)
	case class Property
	(
		name: String,
		values: Seq[PropertyValue]
	)
	val TASK_TYPE_NAME = "task_type"
	def apply(config: SessionConfig): SetTestMetadataData = {
		val taskTypeValues = config.tests.groupBy(_.task_type)
		SetTestMetadataData(props = Seq(Property(
			name = TASK_TYPE_NAME,
			values = (taskTypeValues map {
				case (value, tests) => PropertyValue(
					value = value.toString,
					tests = tests.map(_.identifier)
				)
			}).toSeq
		)))
	}
}

case class SetTestMetadataMessage
(
	override val `type`: MessageType.Value = MessageType.SetTestMetadata,
	data: SetTestMetadataData
) extends MessageData

case class ResumeManagedSessionData
(
	test_session_id: Long,
	study_name: String,
	access_guid: Guid,
	subject_guid: Guid,
	reporting_id: Guid,
	account_subject_id: Long,
	subject_name: String
)

case class ResumeManagedSettingsMessage
(
	override val `type`: MessageType.Value = MessageType.ResumeManagedSession,
	data: ResumeManagedSessionData
) extends MessageData

case class CommandErrorData
(
	command: MessageType.Value,
	error: String
)

case class CommandErrorMessage
(
	override val `type`: MessageType.Value = MessageType.CommandError,
	data: CommandErrorData
) extends MessageData

case class ControllerErrorData
(
	error: String
)

case class ControllerErrorMessage
(
	override val `type`: MessageType.Value = MessageType.ControllerError,
	data: ControllerErrorData
) extends MessageData

case class StartTestData
(
	test_index: Int,
	start_value: Option[String],
	sequence_id: Option[SequenceElement.Id],
	update_seq_pos: Boolean
)

case class StartTestMessage
(
	override val `type`: MessageType.Value = MessageType.StartTest,
	data: StartTestData
) extends MessageData

case class PerformanceData
(
	log_index: Int,
	data: TestRunDetail
)

case class PerformanceDataMessage
(
	override val `type`: MessageType.Value = MessageType.Performance,
	data: PerformanceData
) extends MessageData

case class GetTestDetailData
(
	log_index: Int
)

case class GetTestDetailMessage
(
	override val `type`: MessageType.Value = MessageType.GetTestDetail,
	data: GetTestDetailData
) extends MessageData

case class TestDetailData
(
	log_index: Int,
	data: TestRunDetail
)

case class TestDetailMessage
(
	override val `type`: MessageType.Value = MessageType.TestDetail,
	data: TestDetailData
) extends MessageData

case class CommState
(
	audio_out: Boolean,
	video_out: Boolean,
	audio_in: Boolean,
	video_in: Boolean,
	source_name: String
) {
	def allOff: Boolean = (!audio_out) && (!video_out) && (!audio_in) && (!video_in)
}

case class CommStateMessage
(
	override val `type`: MessageType.Value = MessageType.CommState,
	data: CommState
) extends MessageData

case class GetResumableSessionsData
(
	minimum_start_time: SecondsTimestamp
)

case class GetResumableSessionsMessage
(
	override val `type`: MessageType.Value = MessageType.GetResumableSessions,
	data: GetResumableSessionsData
) extends MessageData

case class ResumableSessionsData
(
	subject_guids: Seq[Guid]
)

case class ResumableSessionsMessage
(
	override val `type`: MessageType.Value = MessageType.ResumableSessions,
	data: ResumableSessionsData
) extends MessageData

object BatteryLevel extends Enumeration {
	val High = Value("high")
	val Medium = Value("medium")
	val Low = Value("low")
	val Critical = Value("critical")
}

case class PowerStatusData
(
	ac_power: Option[Boolean],
	battery_present: Option[Boolean],
	charging: Option[Boolean],
	level: Option[BatteryLevel.Value],
	battery_percent: Option[Int],
	battery_life_seconds: Option[Int]
)

case class PowerStatusMessage
(
	override val `type`: MessageType.Value = MessageType.PowerStatus,
	data: PowerStatusData
) extends MessageData

object NotificationType extends Enumeration {
	val Warning = Value("warning")
	val Error = Value("error")
	val Message = Value("message")
}

case class ExaminerComment
(
	id: Int,
	time: SecondsTimestamp,
	text: String,
	tag: Option[String]
)

case class SetCommentData
(
	comment: ExaminerComment,
	test_log_index: Option[Int],
	add: Boolean
)

case class SetCommentMessage
(
	override val `type`: MessageType.Value = MessageType.SetComment,
	data: SetCommentData
)
extends MessageData

case class SetTagsData
(
	tags: Seq[String],
	test_log_index: Option[Int]
)

case class SetTagsMessage
(
	override val `type`: MessageType.Value = MessageType.SetTags,
	data: SetTagsData
)
extends MessageData

case class NotificationData
(
	`type`: NotificationType.Value,
	message: String
)

case class NotificationMessage
(
	override val `type`: MessageType.Value = MessageType.Notification,
	data: NotificationData
) extends MessageData

case class DataSyncStateData
(
	requests: Seq[SyncRequestStatus],
	reset: Boolean,
	paused: Boolean,
	backlog_count: Int
)

case class DataSyncStateMessage
(
	override val `type`: MessageType.Value = MessageType.DataSyncState,
	data: DataSyncStateData
) extends MessageData

case class SetDataSyncPauseData
(
	paused: Boolean
)

case class SetDataSyncPauseMessage
(
	override val `type`: MessageType.Value = MessageType.SetDataSyncPause,
	data: SetDataSyncPauseData
) extends MessageData

case class GetDataSyncStateData
(
	reset: Boolean
)

case class GetDataSyncStateMessage
(
	override val `type`: MessageType.Value = MessageType.GetDataSyncState,
	data: GetDataSyncStateData
) extends MessageData

case class ShutdownControllerData
(
	shutdown_app: Boolean,
	shutdown_system: Boolean
)

case class ShutdownControllerMessage
(
	override val `type`: MessageType.Value = MessageType.ShutdownController,
	data: ShutdownControllerData
) extends MessageData

case class SetUploadCredentialsData
(
	access_key: String,
	secret_key: String
)

case class SetUploadCredentialsMessage
(
	override val `type`: MessageType.Value = MessageType.SetUploadCredentials,
	data: SetUploadCredentialsData
) extends MessageData


object MessageData {

	// NotificationType
	implicit val notificationTypeDecoder: Decoder[NotificationType.Value] = Decoder.decodeEnumeration(NotificationType)
	implicit val notificationTypeEncoder: Encoder[NotificationType.Value] = Encoder.encodeEnumeration(NotificationType)

	// NotificationType
	implicit val caasTypeDecoder: Decoder[CaasType.Value] = Decoder.decodeEnumeration(CaasType)
	implicit val caasTypeEncoder: Encoder[CaasType.Value] = Encoder.encodeEnumeration(CaasType)

  // MessageType
  implicit val messageTypeDecoder: Decoder[MessageType.Value] = Decoder.decodeEnumeration(MessageType)
  implicit val messageTypeEncoder: Encoder[MessageType.Value] = Encoder.encodeEnumeration(MessageType)

  // TestStatus
  implicit val testStatusDecoder: Decoder[TestStatus.Value] = Decoder.decodeEnumeration(TestStatus)
  implicit val testStatusEncoder: Encoder[TestStatus.Value] = Encoder.encodeEnumeration(TestStatus)

	// PresentationConnectionStatus
	implicit val presentationConnectionStatusDecoder: Decoder[PresentationConnectionStatus.Value] = Decoder.decodeEnumeration(PresentationConnectionStatus)
	implicit val presentationConnectionStatusEncoder: Encoder[PresentationConnectionStatus.Value] = Encoder.encodeEnumeration(PresentationConnectionStatus)

	// ParameterType
	implicit val parameterTypeDecoder: Decoder[ParameterType.Value] = Decoder.decodeEnumeration(ParameterType)
	implicit val parameterTypeEncoder: Encoder[ParameterType.Value] = Encoder.encodeEnumeration(ParameterType)

	// ParameterDataType
	implicit val parameterDataTypeDecoder: Decoder[ParameterDataType.Value] = Decoder.decodeEnumeration(ParameterDataType)
	implicit val parameterDataTypeEncoder: Encoder[ParameterDataType.Value] = Encoder.encodeEnumeration(ParameterDataType)

	// BatteryLevel
	implicit val batteryLevelDecoder: Decoder[BatteryLevel.Value] = Decoder.decodeEnumeration(BatteryLevel)
	implicit val batteryLevelEncoder: Encoder[BatteryLevel.Value] = Encoder.encodeEnumeration(BatteryLevel)

	// TestGraphType
	implicit val testGraphTypeDecoder: Decoder[TestGraphType.Value] = Decoder.decodeEnumeration(TestGraphType)
	implicit val testGraphTypeEncoder: Encoder[TestGraphType.Value] = Encoder.encodeEnumeration(TestGraphType)

	// PauseType
	implicit val pauseTypeDecoder: Decoder[PauseType.Value] = Decoder.decodeEnumeration(PauseType)
	implicit val pauseTypeEncoder: Encoder[PauseType.Value] = Encoder.encodeEnumeration(PauseType)

	// SyncRequestType
	implicit val syncRequestDecoder: Decoder[SyncRequestType.Value] = Decoder.decodeEnumeration(SyncRequestType)
	implicit val syncRequestEncoder: Encoder[SyncRequestType.Value] = Encoder.encodeEnumeration(SyncRequestType)

	// SyncRequestStatus
	implicit val syncRequestStatusDecoder: Decoder[SyncRequestStatus.Value] = Decoder.decodeEnumeration(SyncRequestStatus)
	implicit val syncRequestStatusEncoder: Encoder[SyncRequestStatus.Value] = Encoder.encodeEnumeration(SyncRequestStatus)

	implicit val sexDecoder = Decoder.decodeEnumeration(Sex)
	implicit val sexEncoder = Encoder.encodeEnumeration(Sex)

	def decode(json: String): Option[MessageData] = {
		def getType[T](implicit d: Decoder[T]): Option[T] = parser.decode[T](json) match {
			case Left(_) => None
			case Right(obj) => Some(obj)
		}
		Try(
			getType[BasicMessage] flatMap { basic =>
				basic.`type` match {
					case MessageType.ControllerState => getType[ControllerStateMessage]
					case MessageType.TestPackageData => getType[TestPackageDataMessage]
					case MessageType.SessionData => getType[SessionDataMessage]
					case MessageType.State => getType[StateDataMessage]
					case MessageType.Notification => getType[NotificationMessage]
					case MessageType.CommState => getType[CommStateMessage]
					case MessageType.PowerStatus => getType[PowerStatusMessage]
					case MessageType.SetSubjectAV => getType[SetSubjectAVMessage]
					case MessageType.SetSession => getType[SetSessionMessage]
					case MessageType.CommandError => getType[CommandErrorMessage]
					case MessageType.ControllerError => getType[ControllerErrorMessage]
					case MessageType.StartTest => getType[StartTestMessage]
					case MessageType.Performance => getType[PerformanceDataMessage]
					case MessageType.TestDetail => getType[TestDetailMessage]
					case MessageType.ChatVolume => getType[ChatVolumeMessage]
					case MessageType.SetComment => getType[SetCommentMessage]
					case MessageType.SetTags => getType[SetTagsMessage]
					case MessageType.ResumableSessions => getType[ResumableSessionsMessage]
					case MessageType.DataSyncState => getType[DataSyncStateMessage]
					case _ => Some(basic)
				}
			}
		) match {
			case Success(res) => res
			case Failure(e) => println(s"Failed to decode message '$json': ${e.getLocalizedMessage}"); None
		}
	}
}

class ReceiveMessageAdapter (process: (MessageData) => Unit)(implicit logger: Logger) {
	def receive(msg: String): Unit = {
		val res = MessageData.decode(msg)
		if (res.isEmpty) logger.debug(s"CCAB unrecognized message '$msg'.")
		res.foreach(process(_))
	}
}

class SendMessageAdapter (private var send: (String) => Unit = _ => Unit)(implicit logger: Logger) {
	import MessageData._

	def resetSender(sender: (String) => Unit): Unit = { send = sender }

	def send[T](msg: T)(implicit encoder: Encoder[T]): Unit = {
		val json = jsonPrint(msg)
		logger.debug(s"Sending message with size ${json.size}")
		if (json.size > 7000) {
			logger.warn(s"Message with size ${json.size} may be oversized.")
		}
		send(json)
	}

	def send(t: MessageType.Value): Unit = {
		send(jsonPrint(BasicMessage(`type` = t)))
	}
}